import ky, { type KyResponse } from "ky";

import { type AuthUser, useAuthStore } from "@/stores/auth";

const REFRESH_URL = "auth/refresh";

type RawAuthResponse = { user: AuthUser; token: { access_token: string } };

let refreshInflight: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  try {
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      useAuthStore.getState().clear();
      return null;
    }
    const data = (await res.json()) as RawAuthResponse;
    useAuthStore.getState().setSession(data.user, data.token.access_token);
    return data.token.access_token;
  } catch {
    useAuthStore.getState().clear();
    return null;
  }
}

function refreshOnce(): Promise<string | null> {
  refreshInflight ??= doRefresh().finally(() => {
    refreshInflight = null;
  });
  return refreshInflight;
}

export const api = ky.create({
  prefixUrl: "/api",
  timeout: 15_000,
  retry: { limit: 1, methods: ["get"] },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = useAuthStore.getState().accessToken;
        if (token) request.headers.set("Authorization", `Bearer ${token}`);
      },
    ],
    afterResponse: [
      async (request, _options, response): Promise<KyResponse | undefined> => {
        if (response.status !== 401 || request.url.endsWith(REFRESH_URL)) return undefined;
        const newToken = await refreshOnce();
        if (!newToken) return response;
        const retry = new Request(request, { headers: new Headers(request.headers) });
        retry.headers.set("Authorization", `Bearer ${newToken}`);
        return ky(retry);
      },
    ],
  },
});
